import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { API_CONFIG, getAuthHeader } from '../../api/config';
import { recupereStatsLegrand } from '../../services/legrandServices';
import LegrandAppBar from './LegrandAppBar';
import AssignmentIcon from '@mui/icons-material/Assignment';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import CreditCardIcon from '@mui/icons-material/CreditCard';

const DashboardLegrand: React.FC = () => {
  const [user, setUser] = useState<any>(null);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [visaFilter, setVisaFilter] = useState<'all' | 'visa' | 'evisa'>('all');
  const navigate = useNavigate();

  useEffect(() => {
    // Vérifier l'authentification
    const storedUser = localStorage.getItem('user_legrand');
    if (!storedUser) {
      navigate('/');
      return;
    }
    setUser(JSON.parse(storedUser));

    // Vérifier la validité du token
    verifyToken();

    // Charger les statistiques
    loadStatistics();

    // Recharger les statistiques toutes les 30 secondes
    const interval = setInterval(loadStatistics, 30000);
    return () => clearInterval(interval);
  }, [navigate]);

  // Recharger les statistiques quand le filtre visa/evisa change
  useEffect(() => {
    if (user?.role === 'superadmin') {
      loadStatistics();
    }
  }, [visaFilter]);

  const loadStatistics = async () => {
    try {
      const data = await recupereStatsLegrand(visaFilter);
      setStats(data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
      setLoading(false);
    }
  };

  const verifyToken = async () => {
    try {
      const response = await fetch(`${API_CONFIG.baseUrl}/api/legrand/verify`, {
        headers: {
          ...getAuthHeader('legrand'),
        },
      });

      if (!response.ok) {
        handleLogout();
      }
    } catch (error) {
      console.error('Token verification error:', error);
      handleLogout();
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token_legrand');
    localStorage.removeItem('user_legrand');
    localStorage.removeItem('societe');
    navigate('/');
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <LegrandAppBar
        title="LEGRAND - Admin VisaMonde"
        username={user?.username}
        showDashboardButton={false}
      />

      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          Bienvenue {user?.username}
        </Typography>

        {/* Section Statistiques */}


        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Affichage du nombre total de demandes par type pour superadmin */}
            {user?.role === 'superadmin' && (
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Card sx={{ px: 2, py: 1, bgcolor: visaFilter === 'all' ? '#1976d2' : '#f5f5f5' }}>
                  <Button
                    variant="text"
                    onClick={() => setVisaFilter('all')}
                    sx={{ color: visaFilter === 'all' ? 'white' : 'inherit' }}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6">{stats?.totalAll || 0}</Typography>
                      <Typography variant="caption">Tous</Typography>
                    </Box>
                  </Button>
                </Card>
                <Card sx={{ px: 2, py: 1, bgcolor: visaFilter === 'visa' ? '#1976d2' : '#f5f5f5' }}>
                  <Button
                    variant="text"
                    onClick={() => setVisaFilter('visa')}
                    sx={{ color: visaFilter === 'visa' ? 'white' : 'inherit' }}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6">{stats?.totalVisa || 0}</Typography>
                      <Typography variant="caption">Visa</Typography>
                    </Box>
                  </Button>
                </Card>
                <Card sx={{ px: 2, py: 1, bgcolor: visaFilter === 'evisa' ? '#1976d2' : '#f5f5f5' }}>
                  <Button
                    variant="text"
                    onClick={() => setVisaFilter('evisa')}
                    sx={{ color: visaFilter === 'evisa' ? 'white' : 'inherit' }}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6">{stats?.totalEvisa || 0}</Typography>
                      <Typography variant="caption">eVisa</Typography>
                    </Box>
                  </Button>
                </Card>
              </Box>
            )}

            {/* Statistiques différentes selon le rôle */}
            {user?.role === 'superadmin' ? (
              // Statistiques pour superadmin
              <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={2}>
                  <Card sx={{ bgcolor: '#2196f3', color: 'white' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AssignmentIcon />
                        <Box>
                          <Typography variant="h4">{stats?.dossiersEnCours || 0}</Typography>
                          <Typography variant="body2">En cours</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <Card sx={{ bgcolor: '#ff5722', color: 'white' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ReceiptIcon />
                        <Box>
                          <Typography variant="h4">{stats?.attenteFacture || 0}</Typography>
                          <Typography variant="body2">Attente facture</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <Card sx={{ bgcolor: '#ff9800', color: 'white' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AssignmentIcon />
                        <Box>
                          <Typography variant="h4">{stats?.aFaire || 0}</Typography>
                          <Typography variant="body2">À faire</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <Card sx={{ bgcolor: '#03a9f4', color: 'white' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <HourglassEmptyIcon />
                        <Box>
                          <Typography variant="h4">{stats?.attenteVisa || 0}</Typography>
                          <Typography variant="body2">Attente visa</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <Card sx={{ bgcolor: '#ff9800', color: 'white' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WarningIcon />
                        <Box>
                          <Typography variant="h4">{stats?.dossierEnSouffrance || 0}</Typography>
                          <Typography variant="body2">Dossier en souffrance</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <Card sx={{ bgcolor: '#4caf50', color: 'white' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon />
                        <Box>
                          <Typography variant="h4">{stats?.dossiersTraites || 0}</Typography>
                          <Typography variant="body2">Traités</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <Card sx={{ bgcolor: '#ff6b35', color: 'white' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CreditCardIcon />
                        <Box>
                          <Typography variant="h4">{stats?.ecardsUrgentes || 0}</Typography>
                          <Typography variant="body2">E-cards urgentes</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <Card sx={{ bgcolor: '#9c27b0', color: 'white' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <HourglassEmptyIcon />
                        <Box>
                          <Typography variant="h4">{stats?.attenteReception || 0}</Typography>
                          <Typography variant="body2">Attente réception</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            ) : (
              // Statistiques pour admin
              <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={2.4}>
                  <Card sx={{ bgcolor: '#2196f3', color: 'white' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AssignmentIcon />
                        <Box>
                          <Typography variant="h4">{stats?.dossiersEnCours || 0}</Typography>
                          <Typography variant="body2">Dossiers en cours</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={2.4}>
                  <Card sx={{ bgcolor: '#ff9800', color: 'white' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationOnIcon />
                        <Box>
                          <Typography variant="h4">{stats?.attenteAdresse || 0}</Typography>
                          <Typography variant="body2">Attente adresse</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={2.4}>
                  <Card sx={{ bgcolor: '#f44336', color: 'white' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ReceiptIcon />
                        <Box>
                          <Typography variant="h4">{stats?.attenteFacture || 0}</Typography>
                          <Typography variant="body2">Attente facture</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={2.4}>
                  <Card sx={{ bgcolor: '#9c27b0', color: 'white' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DownloadIcon />
                        <Box>
                          <Typography variant="h4">{stats?.facturesARecuperer || 0}</Typography>
                          <Typography variant="body2">Factures à récupérer</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={2.4}>
                  <Card sx={{ bgcolor: '#4caf50', color: 'white' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon />
                        <Box>
                          <Typography variant="h4">{stats?.dossiersTraites || 0}</Typography>
                          <Typography variant="body2">Dossiers traités</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <Card sx={{ bgcolor: '#ff6b35', color: 'white' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CreditCardIcon />
                        <Box>
                          <Typography variant="h4">{stats?.ecardsUrgentes || 0}</Typography>
                          <Typography variant="body2">E-cards urgentes</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <Card sx={{ bgcolor: '#ff9800', color: 'white' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WarningIcon />
                        <Box>
                          <Typography variant="h4">{stats?.dossierEnSouffrance || 0}</Typography>
                          <Typography variant="body2">Dossier en souffrance</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </>
        )}

        {/* Section Navigation */}
       
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={4}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': { backgroundColor: '#f5f5f5' },
              }}
              onClick={() => navigate('/legrand/demandes')}
            >
              <Typography variant="h6" gutterBottom>
                Gestion des Demandes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Gérer les demandes de visa
              </Typography>
              <Button variant="contained" sx={{ mt: 2 }}>
                Accéder
              </Button>
            </Paper>
          </Grid>

        


          <Grid item xs={12} md={4}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': { backgroundColor: '#f5f5f5' },
              }}
              onClick={() => navigate('/legrand/adresses')}
            >
              <Typography variant="h6" gutterBottom>
                Adresses
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Gérer les adresses
              </Typography>
              <Button variant="contained" sx={{ mt: 2 }}>
                Accéder
              </Button>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': { backgroundColor: '#f5f5f5' },
              }}
              onClick={() => navigate('/legrand/emails')}
            >
              <Typography variant="h6" gutterBottom>
                Emails
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Gérer les emails
              </Typography>
              <Button variant="contained" sx={{ mt: 2 }}>
                Accéder
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default DashboardLegrand;